// components/HubSpotConfigModal.jsx
import React, { useState, useEffect } from "react";
import { X, Save, AlertCircle } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const HubSpotConfigModal = ({ isOpen, onClose, integration, onUpdate }) => {
  const { isDarkMode } = useTheme();
  const [settings, setSettings] = useState({
    syncContacts: false,
    syncDeals: false,
    syncTickets: false,
    syncTasks: false,
    autoSync: false,
    syncFrequency: "manual",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (integration?.syncSettings) {
      setSettings(integration.syncSettings);
    }
  }, [integration]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      await apiService.updateHubSpotSettings(settings);
      onUpdate();
      onClose();
    } catch (err) {
      console.error("Error saving settings:", err);
      setError(
        err.response?.data?.message ||
          "Failed to save settings. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
      <div
        className={`rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border border-gray-700"
            : "bg-white border border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isDarkMode ? "bg-white" : "bg-gray-100 border border-gray-200"
              }`}
            >
              <img
                src="/assets/hubspot.png"
                alt="HubSpot"
                className="w-6 h-6"
              />
            </div>
            <div>
              <h2
                className={`text-lg font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                HubSpot Sync Configuration
              </h2>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Choose what data to sync from HubSpot
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg ${
                isDarkMode
                  ? "bg-red-500/20 border border-red-500/30"
                  : "bg-red-50 border border-red-200"
              }`}
            >
              <AlertCircle
                className={`w-5 h-5 flex-shrink-0 ${
                  isDarkMode ? "text-red-400" : "text-red-600"
                }`}
              />
              <p
                className={`text-sm ${
                  isDarkMode ? "text-red-400" : "text-red-600"
                }`}
              >
                {error}
              </p>
            </div>
          )}

          {/* Info Banner */}
          <div
            className={`p-4 rounded-lg ${
              isDarkMode
                ? "bg-blue-500/20 border border-blue-500/30"
                : "bg-blue-50 border border-blue-200"
            }`}
          >
            <p
              className={`text-sm ${
                isDarkMode ? "text-blue-300" : "text-blue-700"
              }`}
            >
              Select the HubSpot objects you want to sync to your CRM. Data will
              be imported and kept in sync according to your preferences.
            </p>
          </div>

          {/* Sync Options */}
          <div className="space-y-4">
            <h3
              className={`font-medium ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Sync Objects
            </h3>

            {/* Contacts */}
            <div
              className={`flex items-start justify-between p-4 rounded-lg ${
                isDarkMode ? "bg-[#1a1d23]" : "bg-gray-50"
              }`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4
                    className={`font-medium ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Contacts → Leads
                  </h4>
                  <span
                    className={`px-2 py-0.5 text-xs rounded ${
                      isDarkMode
                        ? "bg-purple-900/30 text-purple-300"
                        : "bg-purple-100 text-purple-700"
                    }`}
                  >
                    Recommended
                  </span>
                </div>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Import HubSpot contacts as leads in your CRM. Excludes
                  contacts already marked as customers.
                </p>
                <div
                  className={`mt-2 text-xs ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  Maps to:{" "}
                  <span
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                  >
                    Leads page
                  </span>
                </div>
              </div>
              <button
                onClick={() =>
                  setSettings((prev) => ({
                    ...prev,
                    syncContacts: !prev.syncContacts,
                  }))
                }
                className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                  settings.syncContacts
                    ? isDarkMode
                      ? "bg-[#7F56D9]"
                      : "bg-purple-600"
                    : isDarkMode
                      ? "bg-gray-600"
                      : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.syncContacts ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Deals */}
            <div
              className={`flex items-start justify-between p-4 rounded-lg ${
                isDarkMode ? "bg-[#1a1d23]" : "bg-gray-50"
              }`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4
                    className={`font-medium ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Deals → Opportunities
                  </h4>
                  <span
                    className={`px-2 py-0.5 text-xs rounded ${
                      isDarkMode
                        ? "bg-purple-900/30 text-purple-300"
                        : "bg-purple-100 text-purple-700"
                    }`}
                  >
                    Recommended
                  </span>
                </div>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Import open deals as opportunities. Includes deal stages,
                  amounts, and line items.
                </p>
                <div
                  className={`mt-2 text-xs ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  Maps to:{" "}
                  <span
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                  >
                    Opportunities page
                  </span>
                </div>
              </div>
              <button
                onClick={() =>
                  setSettings((prev) => ({
                    ...prev,
                    syncDeals: !prev.syncDeals,
                  }))
                }
                className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                  settings.syncDeals
                    ? isDarkMode
                      ? "bg-[#7F56D9]"
                      : "bg-purple-600"
                    : isDarkMode
                      ? "bg-gray-600"
                      : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.syncDeals ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Tickets */}
            <div
              className={`flex items-start justify-between p-4 rounded-lg ${
                isDarkMode ? "bg-[#1a1d23]" : "bg-gray-50"
              }`}
            >
              <div className="flex-1">
                <h4
                  className={`font-medium mb-1 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Tickets → Tickets
                </h4>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Import support tickets with status, priority, and assignments.
                </p>
                <div
                  className={`mt-2 text-xs ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  Maps to:{" "}
                  <span
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                  >
                    Tickets page
                  </span>
                </div>
              </div>
              <button
                onClick={() =>
                  setSettings((prev) => ({
                    ...prev,
                    syncTickets: !prev.syncTickets,
                  }))
                }
                className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                  settings.syncTickets
                    ? isDarkMode
                      ? "bg-[#7F56D9]"
                      : "bg-purple-600"
                    : isDarkMode
                      ? "bg-gray-600"
                      : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.syncTickets ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Tasks */}
            <div
              className={`flex items-start justify-between p-4 rounded-lg ${
                isDarkMode ? "bg-[#1a1d23]" : "bg-gray-50"
              }`}
            >
              <div className="flex-1">
                <h4
                  className={`font-medium mb-1 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Tasks → Tasks
                </h4>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Import tasks and to-dos with due dates and assignments.
                </p>
                <div
                  className={`mt-2 text-xs ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  Maps to:{" "}
                  <span
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                  >
                    Tasks page
                  </span>
                </div>
              </div>
              <button
                onClick={() =>
                  setSettings((prev) => ({
                    ...prev,
                    syncTasks: !prev.syncTasks,
                  }))
                }
                className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                  settings.syncTasks
                    ? isDarkMode
                      ? "bg-[#7F56D9]"
                      : "bg-purple-600"
                    : isDarkMode
                      ? "bg-gray-600"
                      : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.syncTasks ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Auto Sync */}
          <div className="space-y-4 pt-4 border-t border-gray-700">
            <h3
              className={`font-medium ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Sync Schedule
            </h3>

            <div
              className={`flex items-start justify-between p-4 rounded-lg ${
                isDarkMode ? "bg-[#1a1d23]" : "bg-gray-50"
              }`}
            >
              <div className="flex-1">
                <h4
                  className={`font-medium mb-1 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Automatic Sync
                </h4>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Enable automatic background sync at regular intervals.
                </p>
              </div>
              <button
                onClick={() =>
                  setSettings((prev) => ({
                    ...prev,
                    autoSync: !prev.autoSync,
                  }))
                }
                className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                  settings.autoSync
                    ? isDarkMode
                      ? "bg-[#7F56D9]"
                      : "bg-purple-600"
                    : isDarkMode
                      ? "bg-gray-600"
                      : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.autoSync ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {settings.autoSync && (
              <div
                className={`p-4 rounded-lg ${
                  isDarkMode ? "bg-[#1a1d23]" : "bg-gray-50"
                }`}
              >
                <label
                  className={`block font-medium mb-2 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Sync Frequency
                </label>
                <select
                  value={settings.syncFrequency}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      syncFrequency: e.target.value,
                    }))
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="manual">Manual only</option>
                  <option value="hourly">Every hour</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
                <p
                  className={`mt-2 text-xs ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  Automatic syncs run in the background and don't interrupt your
                  work.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            onClick={onClose}
            disabled={isSaving}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            } disabled:opacity-50`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`px-4 py-2 text-sm font-medium text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
              isDarkMode
                ? "bg-[#7F56D9] hover:bg-[#6B46C1]"
                : "bg-purple-600 hover:bg-purple-700"
            }`}
          >
            {isSaving ? (
              <>
                <Save className="w-4 h-4 animate-pulse" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HubSpotConfigModal;
